import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, queryAll, state} from "lit/decorators.js";
import {resolve} from "../../container";
import {MacherbusStorage} from "./macherbusStorage";
import {AutoInitializing, UnLitElement, WithEopSlots} from "../../common/elements";
import {Dictionary} from "../../page/elements/dictionary";
import type {EopMacherbusProject} from "./macherbusProject";
import {MACHERBUS_PROJECT_VOTE_EVENT, type MacherbusProjectVoteEvent, type MacherbusProjectVoteEventData} from "./macherbusEvents";
import {trackingButtonInteract} from "../../tracking/trackingAttributes";


const REQUIRED_VOTE_COUNT = 3;

@customElement("eop-macherbus-voting")
export class EopMacherbusVoting extends WithEopSlots(AutoInitializing(UnLitElement)) {

    @property({attribute: "voting-id"})
    public votingId: string;
    @property({attribute: "dialog-id"})
    public checkoutDialogId: string;
    @property({attribute: "projects-slot-name"})
    public projectsSlotName: string;
    @state()
    private votedProjects: string[];
    @queryAll("eop-macherbus-project")
    private projects: NodeListOf<EopMacherbusProject>;

    private dictionary: Dictionary;

    public constructor(private macherbusStorage: MacherbusStorage = resolve(MacherbusStorage)) {
        super();
        this.votedProjects = [];
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);

        this.addEventListener(MACHERBUS_PROJECT_VOTE_EVENT, (event: Event) => {
            const eventDetails = (event as MacherbusProjectVoteEvent).detail;
            this.votedProjects = this.newVotedProjects(eventDetails);
            this.macherbusStorage.saveVotes(this.votingId, this.votedProjects);
        });
    }

    private newVotedProjects(update: MacherbusProjectVoteEventData): string[] {
        const newVotedProjects = Array.from(this.votedProjects);
        if (update.voted) {
            newVotedProjects.push(update.projectId);
        } else {
            newVotedProjects.removeAll(update.projectId);
        }
        return newVotedProjects;
    }

    public render(): TemplateResult {
        const finalizeVotingLabel = this.dictionary.translate("FINALIZE_VOTING_LABEL");
        return html`
            <div class="macherbus-projects">${this.resolveSlots(this.projectsSlotName)}</div>
            <div class="action-footer">
                <div class="invalid-vote-message">${this.invalidVoteMessage()}</div>
                <eop-inline-dialog-button
                        dialog-id=${this.checkoutDialogId}
                        ?disabled-button=${!this.allVoted()}
                        style="primary"
                        label=${finalizeVotingLabel}
                        ${trackingButtonInteract(finalizeVotingLabel)}
                >
                </eop-inline-dialog-button>
            </div>
        `;
    }

    private invalidVoteMessage(): string {
        const voteCount = this.votedProjects.length;
        if (voteCount === REQUIRED_VOTE_COUNT) {
            return "";
        }

        return this.dictionary.translate("WRONG_NUMBER_OF_VOTES_MESSAGE", voteCount === 1 ? "1" : "_", {"[ACTUAL]": voteCount});
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        const voted = this.macherbusStorage.readVotes(this.votingId);
        Array.from(this.projects)
            .filter(p => voted.includes(p.projectId))
            .forEach(p => p.setVoted(true));
    }

    private allVoted(): boolean {
        return this.projects.length > 0
            && this.votedProjects.length === REQUIRED_VOTE_COUNT;
    }
}